import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Circle, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  useAdminDeleteErrorFingerprint,
  useAdminErrorReport,
  useAdminResolveErrorFingerprint,
} from '../api/admin-api'

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function formatCrumbTime(ts: number): string {
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const CRUMB_TYPE_STYLE: Record<string, string> = {
  route: 'bg-blue-100 text-blue-700',
  api: 'bg-amber-100 text-amber-700',
  click: 'bg-slate-100 text-slate-600',
  custom: 'bg-purple-100 text-purple-700',
}

function MetaItem({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-gray-500">{label}</dt>
      <dd className={`mt-1 text-sm text-gray-900 break-all ${mono ? 'font-mono' : ''}`}>
        {value || '-'}
      </dd>
    </div>
  )
}

export function ErrorDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: report, isLoading, error } = useAdminErrorReport(id!)
  const resolveMutation = useAdminResolveErrorFingerprint()
  const deleteMutation = useAdminDeleteErrorFingerprint()

  const handleToggleResolve = () => {
    if (!report) return
    resolveMutation.mutate(report.fingerprint, {
      onSuccess: ({ status }) =>
        toast.success(status === 'resolved' ? '해결됨으로 변경했습니다' : '미해결로 변경했습니다'),
      onError: (err) => toast.error(err.message),
    })
  }

  const handleDelete = () => {
    if (!report) return
    if (!confirm('이 오류를 삭제하시겠습니까? 같은 핑거프린트의 모든 발생이 제거되며 되돌릴 수 없습니다.')) return
    deleteMutation.mutate(report.fingerprint, {
      onSuccess: ({ deleted }) => {
        toast.success(`삭제했습니다 (${deleted}건)`)
        navigate('/admin/errors')
      },
      onError: (err) => toast.error(err.message),
    })
  }

  if (isLoading) return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  if (error) return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  if (!report) return null

  // context 에서 componentStack 은 별도 블록으로 보여준다
  const { componentStack, ...restContext } = (report.context ?? {}) as Record<string, unknown>
  const hasRestContext = Object.keys(restContext).length > 0

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition"
          onClick={() => navigate('/admin/errors')}
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">목록으로</span>
        </button>

        <div className="flex items-center gap-2">
          <Button
            variant={report.status === 'resolved' ? 'secondary' : 'default'}
            size="sm"
            onClick={handleToggleResolve}
            disabled={resolveMutation.isPending}
          >
            {report.status === 'resolved' ? (
              <>
                <Circle className="h-4 w-4" />
                미해결로 변경
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                해결됨으로 표시
              </>
            )}
          </Button>

          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
            삭제
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-bold text-slate-900">{report.name}</h1>
          <Badge className="bg-slate-100 text-slate-600">{report.errorType}</Badge>
          {report.origin === 'staging' && (
            <Badge className="bg-amber-100 text-amber-700">staging</Badge>
          )}
          {report.status === 'resolved' ? (
            <Badge className="bg-green-100 text-green-700">해결됨</Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700">미해결</Badge>
          )}
        </div>
        <p className="mt-2 text-sm text-gray-700 break-all">{report.message}</p>

        <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-3">
          <MetaItem label="사용자" value={report.userEmail} />
          <MetaItem label="발생 시각" value={formatDate(report.createdAt)} />
          <MetaItem label="빌드" value={report.buildId} mono />
          <MetaItem label="URL" value={report.url} />
          <MetaItem label="라우트" value={report.route} />
          <MetaItem label="세션" value={report.sessionId} mono />
          <MetaItem label="언어" value={report.language} />
          <MetaItem label="뷰포트" value={report.viewport} />
          <MetaItem label="핑거프린트" value={report.fingerprint} mono />
        </dl>

        {report.userAgent && (
          <p className="mt-4 text-xs text-gray-400 break-all">{report.userAgent}</p>
        )}
      </div>

      {report.stack && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-900">스택트레이스</h2>
            {report.stackSymbolicated ? (
              <Badge className="bg-green-100 text-green-700">소스맵 복원됨</Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-500">압축됨 (원본)</Badge>
            )}
          </div>
          <pre className="mt-3 max-h-96 overflow-auto rounded-lg bg-slate-950 p-4 text-xs leading-relaxed text-slate-200">
            {report.stackSymbolicated ?? report.stack}
          </pre>
          {report.stackSymbolicated && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                원본(압축) 스택 보기
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">
                {report.stack}
              </pre>
            </details>
          )}
        </div>
      )}

      {typeof componentStack === 'string' && componentStack && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-sm font-semibold text-slate-900">컴포넌트 스택</h2>
          <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-slate-50 p-4 text-xs leading-relaxed text-slate-700">
            {componentStack}
          </pre>
        </div>
      )}

      {report.breadcrumbs && report.breadcrumbs.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-sm font-semibold text-slate-900">브레드크럼 (오래된 순)</h2>
          <ul className="mt-3 space-y-1.5">
            {report.breadcrumbs.map((crumb, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <span className="w-16 shrink-0 pt-0.5 text-xs tabular-nums text-gray-400">
                  {typeof crumb.ts === 'number' ? formatCrumbTime(crumb.ts) : '-'}
                </span>
                <Badge className={`shrink-0 ${CRUMB_TYPE_STYLE[crumb.type] ?? 'bg-slate-100 text-slate-600'}`}>
                  {crumb.type}
                </Badge>
                <span className="break-all text-gray-700">
                  {crumb.message}
                  {crumb.data ? (
                    <span className="ml-2 text-xs text-gray-400">{JSON.stringify(crumb.data)}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasRestContext && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-sm font-semibold text-slate-900">컨텍스트</h2>
          <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-slate-50 p-4 text-xs leading-relaxed text-slate-700">
            {JSON.stringify(restContext, null, 2)}
          </pre>
        </div>
      )}

      {report.occurrences.length > 1 && (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <div className="px-6 pt-6">
            <h2 className="text-sm font-semibold text-slate-900">
              같은 오류의 최근 발생 ({report.occurrences.length}건)
            </h2>
          </div>
          <table className="mt-3 w-full min-w-[600px]">
            <thead className="bg-gray-50 border-y">
              <tr>
                <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">시각</th>
                <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">사용자</th>
                <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">빌드</th>
                <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">환경</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {report.occurrences.map((occurrence) => (
                <tr
                  key={occurrence.id}
                  className={`${occurrence.id === report.id ? 'bg-blue-50/50' : 'hover:bg-gray-50 cursor-pointer'}`}
                  onClick={() => {
                    if (occurrence.id !== report.id) navigate(`/admin/errors/${occurrence.id}`)
                  }}
                >
                  <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {formatDate(occurrence.createdAt)}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-700">{occurrence.userEmail ?? '-'}</td>
                  <td className="px-6 py-3 text-sm font-mono text-gray-500">{occurrence.buildId ?? '-'}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{occurrence.origin}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
